import { useEffect, useState } from "react";
import { http, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { TrendingUp, Euro, Building2, CheckCircle2, XCircle, Loader2 } from "lucide-react";

const COUNT_LABELS = {
  active: "Paganti", ACTIVE_COMPLIMENTARY: "Gratis", TRIAL: "In prova",
  TRIAL_EXPIRED: "Prova scaduta", PAST_DUE: "Insoluti", EXPIRED: "Scaduti", SUSPENDED: "Sospesi",
};

export default function OwnerBilling() {
  const [sum, setSum] = useState(null);
  const [bank, setBank] = useState([]);
  const [plans, setPlans] = useState([]);
  const [edit, setEdit] = useState({});

  const load = () => {
    http.get("/platform/billing/summary").then((r) => setSum(r.data)).catch((e) => toast.error(apiErr(e)));
    http.get("/platform/bank-payments?status=pending_confirmation").then((r) => setBank(r.data)).catch(() => {});
    http.get("/platform/plans").then((r) => setPlans(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const confirmBank = async (id) => { try { await http.post(`/platform/bank-payments/${id}/confirm`); toast.success("Bonifico confermato, azienda attivata"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const rejectBank = async (id) => { try { await http.post(`/platform/bank-payments/${id}/reject`); toast.success("Bonifico rifiutato"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const savePlan = async (p) => {
    const body = {};
    if (edit[`${p.id}_m`] !== undefined) body.price_month = parseFloat(edit[`${p.id}_m`]);
    if (edit[`${p.id}_y`] !== undefined) body.price_year = parseFloat(edit[`${p.id}_y`]);
    try { await http.put(`/platform/plans/${p.id}`, body); toast.success("Piano aggiornato"); load(); } catch (e) { toast.error(apiErr(e)); }
  };

  if (!sum) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6" data-testid="owner-billing">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4" data-testid="metric-mrr">
          <div className="flex items-center gap-2 text-slate-400 text-xs"><TrendingUp className="w-4 h-4" /> MRR</div>
          <div className="text-2xl font-extrabold text-emerald-300 mt-1">€{sum.mrr}</div>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4" data-testid="metric-arr">
          <div className="flex items-center gap-2 text-slate-400 text-xs"><Euro className="w-4 h-4" /> ARR</div>
          <div className="text-2xl font-extrabold text-sky-300 mt-1">€{sum.arr}</div>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4" data-testid="metric-paying">
          <div className="flex items-center gap-2 text-slate-400 text-xs"><Building2 className="w-4 h-4" /> Paganti</div>
          <div className="text-2xl font-extrabold mt-1">{sum.counts.active}</div>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4" data-testid="metric-total">
          <div className="flex items-center gap-2 text-slate-400 text-xs"><Building2 className="w-4 h-4" /> Totale</div>
          <div className="text-2xl font-extrabold mt-1">{sum.total_tenants}</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 text-xs">
        {Object.entries(sum.counts).map(([k, v]) => (
          <span key={k} className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-slate-300">{COUNT_LABELS[k] || k}: <b>{v}</b></span>
        ))}
      </div>

      {/* Pending bank transfers */}
      <div>
        <h3 className="font-heading font-bold mb-2">Bonifici da confermare {bank.length > 0 && <span className="text-sky-300">({bank.length})</span>}</h3>
        {bank.length === 0 ? <p className="text-sm text-slate-500">Nessun bonifico in attesa.</p> : (
          <div className="space-y-2" data-testid="pending-bank-list">
            {bank.map((b) => (
              <div key={b.id} data-testid="bank-payment-row" className="bg-white/5 border border-white/10 rounded-xl p-3 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{b.tenant_name}</div>
                  <div className="text-xs text-slate-400">{b.plan_id} · {b.interval === "year" ? "annuale" : "mensile"} · €{b.amount} {b.reference && `· rif: ${b.reference}`}</div>
                </div>
                <Button size="sm" data-testid={`confirm-bank-${b.id}`} className="bg-emerald-600 hover:bg-emerald-700 h-8" onClick={() => confirmBank(b.id)}><CheckCircle2 className="w-4 h-4 mr-1" /> Conferma</Button>
                <Button size="sm" variant="outline" data-testid={`reject-bank-${b.id}`} className="border-white/20 bg-transparent text-slate-100 hover:bg-white/10 h-8" onClick={() => rejectBank(b.id)}><XCircle className="w-4 h-4 mr-1" /> Rifiuta</Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Plan prices */}
      <div>
        <h3 className="font-heading font-bold mb-2">Prezzi piani (€)</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {plans.map((p) => (
            <div key={p.id} className="bg-white/5 border border-white/10 rounded-xl p-3" data-testid={`plan-edit-${p.id}`}>
              <div className="font-semibold text-sm mb-2">{p.name}</div>
              <div className="flex items-center gap-2">
                {p.interval === "month" || p.price_month != null ? (
                  <label className="text-xs text-slate-400 flex items-center gap-1">Mese <Input type="number" defaultValue={p.price_month ?? ""} onChange={(e) => setEdit({ ...edit, [`${p.id}_m`]: e.target.value })} className="w-20 h-8 bg-white/5 border-white/20" /></label>
                ) : null}
                {p.interval === "year" || p.price_year != null ? (
                  <label className="text-xs text-slate-400 flex items-center gap-1">Anno <Input type="number" defaultValue={p.price_year ?? ""} onChange={(e) => setEdit({ ...edit, [`${p.id}_y`]: e.target.value })} className="w-20 h-8 bg-white/5 border-white/20" /></label>
                ) : null}
                <Button size="sm" className="bg-sky-500 hover:bg-sky-400 text-[#0B1120] h-8" onClick={() => savePlan(p)}>Salva</Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
